import { jsx as _jsx } from "react/jsx-runtime";
import { ADDRESS_SUGGESTION_MIN_LENGTH } from '@kanos/domain';
import { SuggestField } from '@kanos/ui';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { api } from './api.js';
const Source = createContext(null);
/**
 * Turns address suggestions on for every address field below (ADR-073), asked of the API route
 * of `audience` - each app sets it once around all its pages. They come from Kanos' own Photon,
 * so a fragment of a client's address reaches no third party. Without it the street is a plain
 * text field.
 */
export function AddressSuggestionsProvider({ audience, children, }) {
    return _jsx(Source.Provider, { value: audience, children: children });
}
/** Whose route address suggestions are asked of here; `null`: none here. */
export const useAddressSuggestions = () => useContext(Source);
/** How long typing has to pause before a search goes out. */
const PAUSE_MS = 250;
/** „Domstraße 1“ first and plain, „50667 Köln, Deutschland“ after it: the town tells two apart. */
const itemOf = (suggestion) => {
    const [head = suggestion.label, ...rest] = suggestion.label.split(', ');
    return { id: suggestion.label, label: head, detail: rest.join(', '), suggestion };
};
async function ask(audience, q, signal) {
    const request = { params: { query: { q } }, signal };
    const { data } = audience === 'firm'
        ? await api.GET('/api/v1/firm/address-suggestions', request)
        : await api.GET('/api/v1/portal/address-suggestions', request);
    // Refused, limited, gone: no suggestions - the address is typed by hand.
    return data?.items ?? [];
}
/**
 * The street of an address with suggestions while typing (ADR-073, after Corly): from three
 * characters, after a pause of 250 ms, the newest search only; a search asked once in this field
 * is not asked again. A pick fills street, postcode, place and country - all stay editable.
 */
export function AddressAutocomplete({ audience, label, placeholder, value, onChange, onPick, isRequired, maxLength, validate, }) {
    const [items, setItems] = useState([]);
    const timer = useRef(undefined);
    const inFlight = useRef(null);
    const known = useRef(new Map());
    useEffect(() => () => {
        clearTimeout(timer.current);
        inFlight.current?.abort();
    }, []);
    const type = (next) => {
        onChange(next);
        clearTimeout(timer.current);
        inFlight.current?.abort();
        const query = next.trim();
        if (query.length < ADDRESS_SUGGESTION_MIN_LENGTH) {
            setItems([]);
            return;
        }
        const key = query.toLowerCase();
        const cached = known.current.get(key);
        if (cached) {
            setItems(cached);
            return;
        }
        timer.current = setTimeout(() => {
            const controller = new AbortController();
            inFlight.current = controller;
            ask(audience, query, controller.signal).then((found) => {
                const fresh = found.map(itemOf);
                known.current.set(key, fresh);
                setItems(fresh);
            }, () => {
                // Cancelled by a newer search, or Kanos out of reach: typed by hand then.
                if (!controller.signal.aborted)
                    setItems([]);
            });
        }, PAUSE_MS);
    };
    return (_jsx(SuggestField, { label: label, placeholder: placeholder, value: value, onChange: type, suggestions: items, onPick: ({ suggestion }) => {
            setItems([]);
            onPick({
                street: suggestion.street || value,
                postalCode: suggestion.postalCode,
                city: suggestion.city,
                countryCode: suggestion.countryCode,
            });
        }, 
        // The data are OpenStreetMap's: the ODbL asks for the naming, wherever the list shows.
        footnote: "Vorschl\u00E4ge: Photon, Daten \u00A9 OpenStreetMap-Mitwirkende", ...(isRequired === undefined ? {} : { isRequired }), ...(maxLength === undefined ? {} : { maxLength }), ...(validate ? { validate } : {}) }));
}
//# sourceMappingURL=address-suggestions.js.map